import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FiPieChart,
  FiUsers,
  FiShoppingCart,
  FiPackage,
  FiTrendingUp,
  FiMessageSquare,
  FiSettings,
  FiLogOut,
  FiDollarSign
} from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';

/**
 * Sidebar Component
 * Left navigation sidebar matching the Sales Dashboard design
 */
const Sidebar = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', icon: FiPieChart, label: 'Dashboard' },
    { path: '/customers', icon: FiUsers, label: 'Customers List' },
    { path: '/lending', icon: FiShoppingCart, label: 'Lending History' },
    { path: '/payment-plans', icon: FiDollarSign, label: 'Payment Plans' },
    { path: '/refer-merchant', icon: FiTrendingUp, label: 'Refer Merchant' },
    { path: '/support', icon: FiMessageSquare, label: 'Support' },
    { path: '/advertise', icon: FiPackage, label: 'Advertise Now' },
    { path: '/settings', icon: FiSettings, label: 'Settings' },
  ];

  const getSubscriptionDisplay = () => {
    if (!user?.subscription) return 'Free';
    const { package: pkg } = user.subscription;
    if (pkg === 'trial') return 'Trial';
    if (pkg === 'base') return 'Base';
    if (pkg === 'basic_plus') return 'Pro';
    return 'Free';
  };

  return (
    <div className="w-64 h-screen bg-white shadow-lg flex flex-col fixed left-0 top-0 z-50">
      {/* Brand Logo */}
      <div className="p-6 border-b border-neutral-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
            <FiDollarSign className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-display font-bold text-neutral-900">
            BBBooks
          </span>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 py-6 px-3 overflow-y-auto">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 rounded-button transition-all duration-200 ${
                  isActive
                    ? 'bg-primary-500 text-white shadow-button'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-neutral-500'}`} />
                  <span className={`font-medium ${isActive ? 'text-white' : 'text-neutral-700'}`}>
                    {item.label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* Logout Button */}
        <div className="mt-4 pt-4 border-t border-neutral-200">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 rounded-button text-neutral-600 hover:bg-neutral-100 transition-colors w-full"
          >
            <FiLogOut className="w-5 h-5 text-neutral-500" />
            <span className="font-medium text-neutral-700">Sign Out</span>
          </button>
        </div>
      </nav>

      {/* Upgrade Section */}
      <div className="p-4 m-4 bg-gradient-to-br from-primary-500 to-primary-700 rounded-card text-white">
        <div className="text-center mb-3">
          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-3">
            <FiTrendingUp className="w-6 h-6" />
          </div>
          <h3 className="font-bold text-sm mb-1">
            BBBooks {getSubscriptionDisplay()}
          </h3>
          <p className="text-xs opacity-90">
            Get access to all features on Premium
          </p>
        </div>
        {user?.subscription?.package !== 'basic_plus' && (
          <button
            onClick={() => navigate('/select-package')}
            className="w-full bg-white text-primary-600 font-semibold py-2 px-4 rounded-button text-sm hover:bg-opacity-90 transition-colors"
          >
            Get Pro
          </button>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
